import { Box, Button, ButtonGroup, Card, Heading, Paragraph, Text } from '@zillow/constellation-10';

import type { SystemStyleObject } from '@/styled-system/types';
import type { ReactNode } from 'react';

type EmptyStateAction = {
  label: string;
  href?: string;
  onClick?: () => void;
};

type EmptyStateProps = {
  icon: ReactNode;
  heading: string;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  body?: ReactNode;
  primaryAction?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  actions?: ReactNode;
  footer?: string;
  align?: 'center' | 'left';
  useCard?: boolean;
  css?: SystemStyleObject;
  'data-testid'?: string;
};

const renderButton = (action: EmptyStateAction, emphasis: 'filled' | 'outlined') => {
  if (action.href) {
    return (
      <Button tone="brand" emphasis={emphasis} asChild>
        <a href={action.href}>{action.label}</a>
      </Button>
    );
  }
  return (
    <Button tone="brand" emphasis={emphasis} onClick={action.onClick}>
      {action.label}
    </Button>
  );
};

const EmptyState = ({
  icon,
  heading,
  headingLevel = 2,
  body,
  primaryAction,
  secondaryAction,
  actions,
  footer,
  align = 'left',
  useCard = true,
  css: cssProp,
  'data-testid': dataTestId,
}: EmptyStateProps) => {
  const isCentered = align === 'center';
  const hasActions = !!(actions || primaryAction || secondaryAction);
  const hasBothActions = !!(primaryAction && secondaryAction);

  const content = (
    <>
      <Box aria-hidden="true" css={{ marginBottom: 'default' }}>
        {icon}
      </Box>

      <Heading level={headingLevel} textStyle="heading-xs" css={{ marginBottom: body ? 'tightest' : 'default' }}>
        {heading}
      </Heading>

      {body && <Paragraph css={{ marginBottom: hasActions ? 'default' : undefined }}>{body}</Paragraph>}

      {actions}

      {!actions && hasBothActions && (
        <ButtonGroup
          aria-label="Empty state actions"
          orientation="horizontal"
          css={{
            flexDirection: { base: 'column', md: isCentered ? 'row-reverse' : 'row' },
            alignItems: { base: 'stretch', md: 'start' },
            ...(isCentered && { justifyContent: { md: 'center' } }),
          }}
        >
          {renderButton(primaryAction, 'filled')}
          {renderButton(secondaryAction, 'outlined')}
        </ButtonGroup>
      )}
      {!actions && !hasBothActions && primaryAction && renderButton(primaryAction, 'filled')}
      {!actions && !hasBothActions && secondaryAction && renderButton(secondaryAction, 'outlined')}

      {footer && (
        <Text css={{ marginTop: 'default', display: 'block' }} textStyle="fineprint">
          {footer}
        </Text>
      )}
    </>
  );

  if (!useCard) {
    return (
      <Box data-testid={dataTestId} css={{ textAlign: isCentered ? 'center' : 'left' }}>
        {content}
      </Box>
    );
  }

  return (
    <Card
      tone="neutral"
      elevated={false}
      data-testid={dataTestId}
      css={{ paddingInline: 'looser', paddingBlock: 'looser', textAlign: isCentered ? 'center' : 'left', ...cssProp }}
    >
      {content}
    </Card>
  );
};

export default EmptyState;
